import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Home, BookOpen, Flame, TrendingUp, LayoutGrid,
  Wallet, Trophy, Award, UserCircle2, MessageSquare, LogOut,
  Users, Rss, Timer, Layers, Sparkles, Settings as SettingsIcon,
} from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import SuspendedAccessDialog from "@/components/SuspendedAccessDialog";
import BrandLogo from "@/components/BrandLogo";
import NotificationsBell from "@/components/NotificationsBell";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useStudentNotifications } from "@/hooks/useStudentNotifications";

// Primary bottom-nav (5 max — required minimum touch target 44px).
const PRIMARY_TABS = [
  { to: "/student",                icon: Home,        label: "Home",   end: true,  testid: "home" },
  { to: "/student/courses",        icon: BookOpen,    label: "Courses",            testid: "courses" },
  { to: "/student/study",          icon: Flame,       label: "Study",              testid: "study" },
  { to: "/student/progress-card",  icon: TrendingUp,  label: "Progress",           testid: "progress" },
];

// Items shown inside the "More" bottom sheet — grid layout.
const MORE_ITEMS = [
  { to: "/student/smart-review", icon: Sparkles,      label: "Smart Review", desc: "FSRS-personalized flashcards", testid: "smart-review" },
  { to: "/student/mock-exam",   icon: Timer,          label: "Mock Exam",    desc: "Board-style timed exam", testid: "mock-exam" },
  { to: "/student/abstract-exam", icon: Layers,       label: "Abstract Exam", desc: "Figural reasoning practice", testid: "abstract-exam" },
  { to: "/student/enrollments", icon: BookOpen,       label: "Enrollments",  desc: "My courses & free trials", testid: "enrollments" },
  { to: "/student/friends",     icon: Users,          label: "Friends",     desc: "QR codes & study buddies", testid: "friends" },
  { to: "/student/feed",        icon: Rss,            label: "Feed",        desc: "Friend activity",      testid: "feed" },
  { to: "/student/enrollments", icon: Wallet,         label: "Billing & Access", desc: "Subscriptions & payments", testid: "billing" },
  { to: "/student/leaderboard", icon: Trophy,         label: "Leaderboard", desc: "See your rank",        testid: "leaderboard" },
  { to: "/student/badges",      icon: Award,          label: "Badges",      desc: "Your achievements",    testid: "badges" },
  { to: "/student/profile",     icon: UserCircle2,    label: "Profile",     desc: "Account details",      testid: "profile" },
  { to: "/student/settings",    icon: SettingsIcon,   label: "Settings",    desc: "Privacy & preferences", testid: "settings" },
  { to: "/student/support",     icon: MessageSquare,  label: "Support",     desc: "Get help",             testid: "support" },
];

// Paths reachable only from the "More" sheet — when active, highlight "More".
const MORE_PATHS = new Set(MORE_ITEMS.map((m) => m.to));

export default function MobileStudentLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const notif = useStudentNotifications();
  const initial = (user?.full_name || "?").charAt(0).toUpperCase();
  const moreActive = MORE_PATHS.has(location.pathname);
  const [billingAttention, setBillingAttention] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.get("/student/my-enrollments")
      .then((r) => {
        if (cancelled) return;
        const rows = (r.data && r.data.enrolled) || [];
        setBillingAttention(rows.some((c) => c.suspended || c.auto_expired || c.expiring_soon));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#F4F6FB] dark:bg-slate-950" data-testid="mobile-student-layout">
      {/* Sticky Premium Header — pt-safe clears iOS notch / dynamic island */}
      <header className="sticky top-0 z-30 px-4 flex items-center justify-between bg-white/95 dark:bg-slate-900/95 backdrop-blur border-b border-slate-100 dark:border-slate-800 pt-safe">
      <div className="h-14 w-full flex items-center justify-between">
        <div className="flex items-center">
          <BrandLogo size="md" />
        </div>
        <div className="flex items-center gap-1.5">
          <ThemeToggle />
          <NotificationsBell testId="mobile-bell-btn" />
          <button
            type="button"
            onClick={() => nav("/student/profile")}
            className="h-10 w-10 grid place-items-center rounded-full text-white font-display font-extrabold text-sm shadow-md shadow-indigo-200 dark:shadow-none active:scale-95 transition-transform"
            style={{ background: "linear-gradient(135deg,#4F46E5,#7C3AED)" }}
            data-testid="mobile-avatar-btn"
            aria-label="Profile"
          >
            {initial}
          </button>
        </div>
      </div>
      </header>

      <main className="flex-1 px-4 pt-4 pb-28 overflow-y-auto">
        <div className="ipad-container">
          <SuspendedAccessDialog />
          <Outlet />
        </div>
      </main>

      {/* Premium Floating Bottom Navigation — 5 tabs incl. More */}
      <nav
        className="fixed bottom-0 inset-x-0 z-40 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 grid grid-cols-5 rounded-t-2xl shadow-[0_-4px_20px_-4px_rgba(15,23,42,0.08)] dark:shadow-[0_-4px_20px_-4px_rgba(0,0,0,0.5)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-bottom-nav"
      >
        {PRIMARY_TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            onClick={() => setMoreOpen(false)}
            className={({ isActive }) =>
              `relative flex flex-col items-center justify-center gap-1 py-2.5 min-h-[58px] text-[10px] font-bold uppercase tracking-wider ${
                isActive && !moreActive
                  ? "text-violet-700 dark:text-violet-300"
                  : "text-slate-500 dark:text-slate-400"
              }`
            }
            data-testid={`mobile-nav-${t.testid}`}
          >
            {({ isActive }) => {
              const reallyActive = isActive && !moreActive;
              return (
                <>
                  {reallyActive && (
                    <span className="absolute top-1 left-1/2 -translate-x-1/2 h-1 w-8 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500" />
                  )}
                  <t.icon
                    className={`w-5 h-5 ${reallyActive ? "" : "text-slate-400 dark:text-slate-500"}`}
                    strokeWidth={reallyActive ? 2.25 : 1.75}
                  />
                  <span className="text-[9px]">{t.label}</span>
                </>
              );
            }}
          </NavLink>
        ))}

        {/* "More" tab — triggers bottom sheet, highlights when on a More page */}
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          className={`relative flex flex-col items-center justify-center gap-1 py-2.5 min-h-[58px] text-[10px] font-bold uppercase tracking-wider ${
            moreActive || moreOpen
              ? "text-violet-700 dark:text-violet-300"
              : "text-slate-500 dark:text-slate-400"
          }`}
          data-testid="mobile-nav-more"
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
        >
          {(moreActive || moreOpen) && (
            <span className="absolute top-1 left-1/2 -translate-x-1/2 h-1 w-8 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500" />
          )}
          <div className="relative">
            <LayoutGrid
              className={`w-5 h-5 ${moreActive || moreOpen ? "" : "text-slate-400 dark:text-slate-500"}`}
              strokeWidth={moreActive || moreOpen ? 2.25 : 1.75}
            />
            {notif.has_unread && (
              <span
                className="absolute -top-1 -right-1.5 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white dark:ring-slate-900 animate-pulse"
                data-testid="more-unread-dot"
                aria-label="You have new notifications"
              />
            )}
          </div>
          <span className="text-[9px]">More</span>
        </button>
      </nav>

      {/* "More" Bottom Sheet */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="rounded-t-3xl border-t-0 p-0 bg-white dark:bg-slate-900 max-h-[90dvh] h-[90dvh] flex flex-col overflow-hidden"
          data-testid="mobile-more-sheet"
        >
          {/* Drag handle for "swipe down to close" affordance (Radix dialog doesn't
              actually drag, but the visual handle signals dismissibility). */}
          <div className="pt-3 pb-1 flex justify-center shrink-0">
            <span className="h-1.5 w-12 rounded-full bg-slate-200 dark:bg-slate-700" />
          </div>

          <div className="px-5 pb-2 pr-12 shrink-0">
            <h2 className="font-display text-xl font-extrabold tracking-tight text-slate-900 dark:text-slate-50">
              More Options
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Access your other PrepMaster tools
            </p>
          </div>

          {/* Scrollable grid — the sheet is a flex column so this middle
              section takes the remaining space between the header (shrink-0)
              and the footer (shrink-0), enabling native touch-scroll on
              Android where MORE_ITEMS + footer used to overflow off-screen. */}
          <div className="px-5 pt-3 pb-4 grid grid-cols-2 gap-3 overflow-y-auto flex-1 min-h-0 overscroll-contain">
            {MORE_ITEMS.map((item) => {
              const unread =
                (item.testid === "badges" && notif.unread_badges > 0) ||
                (item.testid === "support" && notif.unread_support_replies > 0);
              const count =
                item.testid === "badges" ? notif.unread_badges
                : item.testid === "support" ? notif.unread_support_replies
                : 0;
              const attentionDot = item.testid === "billing" && billingAttention;
              return (
                <button
                  key={item.to}
                  type="button"
                  onClick={() => { setMoreOpen(false); nav(item.to); }}
                  className="group relative flex flex-col items-start gap-2 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 text-left transition-all active:scale-[0.97] hover:border-indigo-300 dark:hover:border-indigo-500 hover:shadow-md min-h-[110px]"
                  data-testid={`more-item-${item.testid}`}
                >
                  <span className="relative h-10 w-10 grid place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-sm">
                    <item.icon className="w-5 h-5" strokeWidth={2} />
                    {unread && (
                      <span
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-[10px] font-extrabold text-white grid place-items-center ring-2 ring-white dark:ring-slate-800"
                        data-testid={`more-item-${item.testid}-badge`}
                      >
                        {count > 9 ? "9+" : count}
                      </span>
                    )}
                    {attentionDot && (
                      <span
                        className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-amber-500 ring-2 ring-white dark:ring-slate-800"
                        data-testid="more-item-billing-attention"
                        aria-label="Billing attention needed"
                      />
                    )}
                  </span>
                  <div>
                    <div className="font-display text-sm font-bold text-slate-900 dark:text-slate-50 leading-tight">
                      {item.label}
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">
                      {item.desc}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Divider + logout row */}
          <div className="px-5 pt-1 pb-5 border-t border-slate-100 dark:border-slate-800 mt-1 shrink-0">
            {/* Theme row — labeled variant so students can spot it clearly */}
            <div className="flex items-center justify-between gap-3 pb-3">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-slate-400 dark:text-slate-500">
                  Appearance
                </div>
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Night Mode
                </div>
              </div>
              <ThemeToggle variant="pill" />
            </div>
            <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-slate-400 dark:text-slate-500">
                  Signed in as
                </div>
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate">
                  {user?.full_name || user?.email || "Student"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setMoreOpen(false); logout(); nav("/login"); }}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-bold uppercase tracking-wider bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-colors"
                data-testid="more-sheet-logout"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
            {/* Safe area padding for iOS home indicator */}
            <div style={{ paddingBottom: "env(safe-area-inset-bottom)" }} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
